import { Button, message, Space, SpinProps, Table, Tabs, Typography } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { PriceBookEntriesTable } from 'components/organisms/PriceBookEntriesTable';
import { Stack } from 'components/UI';
import { RateLimiter } from 'limiter';
import { PriceBookEntry } from 'models/PriceBookEntry';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetBusinessQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useCreatePriceBookEntryMutation } from 'redux/services/julia/juliaApi';
import { setErrorItems, setImportPriceBookEntries, setSuccessItems } from 'redux/slices/importSlice';
import { useAppSelector } from 'redux/store';
import * as xlsx from 'xlsx';

const { TabPane } = Tabs;
const { Text } = Typography;

export const ImportPriceBookEntries: React.FC = () => {
  const { id: entityId } = useParams();
  const navigate = useNavigate();

  const { importPriceBookEntries, errorItems } = useAppSelector((state) => state.import);

  const dispatch = useDispatch();

  const [activeTab, _setActiveTab] = useState('success');
  const [_successItems, _setSuccessItems] = useState<PriceBookEntry[]>([]);
  const [_errorItems, _setErrorItems] = useState<{ productNumber: string; errors: string[] }[]>(errorItems);
  const isImporting = useRef(false);

  const { data: business } = useGetBusinessQuery(entityId as string, { skip: !entityId });

  const dataAreaId = business?.dataAreaId || '';
  const businessId = business?.id || '';

  const importTotal = importPriceBookEntries.length;

  const [createPriceBookEntry] = useCreatePriceBookEntryMutation();

  /* ******************** Functions ******************** */
  const handleNavigation = (activeKey: string): void => {
    _setActiveTab(activeKey);
  };

  const handleNavigateBack = (): void => {
    navigate(`/business/${entityId}/price-book`);
  };

  const handleDownloadErrors = async (): Promise<void> => {
    const worksheet = xlsx.utils.json_to_sheet(_errorItems.map((item) => ({ ...item, errors: item.errors.join('\n') })));
    const workbook = xlsx.utils.book_new();

    xlsx.utils.book_append_sheet(workbook, worksheet, 'default');
    const excelBuffer = await xlsx.write(workbook, { bookType: 'xlsx', bookSST: false, type: 'array' });
    const excelBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const uri = window.URL.createObjectURL(excelBlob);
    const link = document.createElement('a');

    link.setAttribute('href', uri);
    link.setAttribute('download', 'price-book-import-errors.xlsx');

    link.click();
    URL.revokeObjectURL(uri);
  };

  /* ****************** Variables ****************** */
  const importLoadingConfig: SpinProps = {
    indicator: (
      <Space style={{ width: 400, marginLeft: -200 }} size="small">
        <BlockLoader direction="loader loader--slideUp" />
        <Text>{`Importing ${_successItems.length + _errorItems.length} of ${importTotal}`}</Text>
      </Space>
    ),
    spinning: importTotal !== _successItems.length + _errorItems.length,
    wrapperClassName: importTotal !== _successItems.length + _errorItems.length ? 'list-loading' : ''
  };

  const importPriceBookItems = useCallback(async (): Promise<void> => {
    if (entityId && dataAreaId && businessId) {
      const limiter = new RateLimiter({ tokensPerInterval: 25, interval: 1500 });

      isImporting.current = true;

      for (const entry of importPriceBookEntries) {
        await limiter.removeTokens(1);

        createPriceBookEntry({ dataAreaId, businessId, payload: entry })
          .unwrap()
          .then((created) => {
            _setSuccessItems((prevState) => [...prevState, created]);
          })
          .catch((error) => {
            if ('data' in error) {
              _setErrorItems((prevState) => [...prevState, { productNumber: entry.productNumber, errors: [typeof error.data === 'string' ? error.data : JSON.stringify(error.data)] }]);
            } else {
              _setErrorItems((prevState) => [...prevState, { productNumber: entry.productNumber, errors: [error.message] }]);
            }
          });
      }
    }
  }, [entityId, dataAreaId, businessId, importPriceBookEntries, createPriceBookEntry]);

  /* ****************** Use Effects ****************** */
  useEffect(() => {
    if (!isImporting.current && dataAreaId && businessId) {
      importPriceBookItems();
    }
  }, [importPriceBookItems, dataAreaId, businessId]);

  useEffect(() => {
    if (_successItems.length + _errorItems.length === importTotal && importTotal > 0) {
      dispatch(setSuccessItems(_successItems as any));
      message.info('Import finished processing');
    }
  }, [_errorItems.length, _successItems, _successItems.length, dispatch, importTotal]);

  useEffect(() => {
    return (): void => {
      dispatch(setSuccessItems([]));
      dispatch(setErrorItems([]));
      dispatch(setImportPriceBookEntries([]));
    };
  }, [dispatch]);

  /* ****************** Render ****************** */
  return (
    <Stack flexDirection="column">
      <Stack justifyContent="space-between">
        <Typography.Title level={3}>Import Price Book Entries</Typography.Title>
        <Stack>
          <Button onClick={handleNavigateBack}>Back to Price Book</Button>
          <Button onClick={handleDownloadErrors} type="primary" disabled={!_errorItems.length}>
            Export Errors
          </Button>
        </Stack>
      </Stack>
      <Tabs defaultActiveKey={activeTab} onChange={handleNavigation} type="card">
        <TabPane tab="Success" key="success">
          <PriceBookEntriesTable dataSource={_successItems} loading={importLoadingConfig} />
        </TabPane>
        <TabPane tab="Errors" key="errors">
          <Table
            size="small"
            dataSource={_errorItems}
            loading={importLoadingConfig}
            columns={[
              {
                dataIndex: 'productNumber',
                title: (
                  <Text ellipsis strong>
                    Product Number
                  </Text>
                ),
                key: 'productNumber',
                width: '30ch'
              },
              {
                dataIndex: 'errors',
                title: (
                  <Text ellipsis strong>
                    Error Message
                  </Text>
                ),
                key: 'errors',
                render: (value: string[]): JSX.Element => (
                  <Stack flexDirection="column">
                    {value.map((item, index) => (
                      <Text key={index}>{item}</Text>
                    ))}
                  </Stack>
                )
              }
            ]}
            rowKey={(_, index): string => `error-${index}`}
          />
        </TabPane>
      </Tabs>
    </Stack>
  );
};
